import { PlatformDependencies } from '@citrite/workspace-ui-platform';
import { refreshAllResources } from 'Environment/callbacks';
import { isChromeAppOrChromeOS, isNativeClient } from 'Environment/launchResource/device';
import {
	getFromLocalStorage,
	removeFromLocalStorage,
	setInLocalStorage,
} from 'javascript/sf/Storage';
import { isAppProtectEnabledByOnPremAdmin } from 'Workspace/AppContext/onpremAppProtectionUtils';
import { container } from 'Workspace/DependencyManagement';

const clientDetectionKey = 'clientDetectionKey';
const previousClientDetectionKey = 'previousClientDetectionKey';
const clientVersionKey = 'clientVersionKey';
const saasLaunchSupportKey = 'saasLaunchSupportKey';

export enum clientType {
	html5 = 'html5',
	native = 'native',
	browserextension = 'browserextension',
	chromeapp = 'chromeapp',
}

export function getClientType() {
	return getFromLocalStorage<clientType>(clientDetectionKey);
}

//allow-unused-export: this export is needed for unit test
export function shouldRefreshResourcesOnClientChange(
	currentClientType: clientType,
	newClientType: clientType
) {
	const clientTypesDoesNotNeedAppProtectionHeader = [
		clientType.html5,
		clientType.chromeapp,
	];

	if (!IS_ON_PREM || isChromeAppOrChromeOS() || isNativeClient()) {
		return false;
	}
	const workspaceConfiguration = container
		.resolve(PlatformDependencies.WorkspaceConfiguration)
		.get();

	// Check if app protection is enabled by admin
	if (!isAppProtectEnabledByOnPremAdmin(workspaceConfiguration)) {
		return false;
	}

	// Handle transitions between groups
	if (currentClientType && newClientType) {
		// Check if the client types belong to the skip group
		const isCurrentInSkipGroup =
			clientTypesDoesNotNeedAppProtectionHeader.includes(currentClientType);
		const isNewInSkipGroup =
			clientTypesDoesNotNeedAppProtectionHeader.includes(newClientType);
		return isCurrentInSkipGroup !== isNewInSkipGroup;
	}

	// Handle cases where there is no current client type but a new one exists. In case of transition from html5 to native state changes from
	// html5 to null to native
	const isGoingFromNullToValidClient =
		currentClientType === null && newClientType !== null;
	return isGoingFromNullToValidClient;
}

export function setClientType(
	type: clientType,
	version?: string,
	supportsSaasLaunch?: string
) {
	const currentClientType = getClientType();
	setInLocalStorage(clientDetectionKey, type);
	if (version) {
		setInLocalStorage(clientVersionKey, version);
	} else {
		removeFromLocalStorage(clientVersionKey);
	}
	if (supportsSaasLaunch) {
		setInLocalStorage(saasLaunchSupportKey, supportsSaasLaunch);
	} else {
		removeFromLocalStorage(saasLaunchSupportKey);
	}
	if (shouldRefreshResourcesOnClientChange(currentClientType, type)) {
		refreshAllResources();
	}
}

export function setPreviousClientType(type: clientType) {
	setInLocalStorage(previousClientDetectionKey, type);
}

export function getSaasLaunchCapability() {
	return getFromLocalStorage<string>(saasLaunchSupportKey);
}

export function getClientVersion() {
	// https://issues.citrite.net/browse/WSUI-10235
	// Linux Native client builds are sending version as intetger 9909 which messes the code
	// which tries parse the client version strings. So, we are converting version to string.
	// Also ensure we maintain the null\undefined semantics for version.
	const clientVersion = getFromLocalStorage<string>(clientVersionKey);
	return clientVersion ? clientVersion.toString() : clientVersion;
}

export function getPreviousClientType() {
	return getFromLocalStorage<clientType>(previousClientDetectionKey);
}
